/**
 * The Reach block of M2's save record (M3 plan R22k, §7 item 9). Save format v2 adds `reach: ReachState`; a v1
 * record (from before the bridge) migrates with a fresh Reach state for its galaxy seed (`galaxySeed`, else
 * `reach-state.json defaultGalaxySeed`, the canonical Reach): the default knowledge, home at the home cluster's hub
 * and no stand-in career (M2 owns the career, D-TBD 41). Loads still resume docked at `career.lastDockedStation`.
 */
import { generateGalaxy } from '../galaxy/generate'
import { buildSystemLayout } from '../galaxy/layout'
import type { SystemLayout } from '../galaxy/layout/types'
import { createReachState, loadReachStateConfig } from '../reach/state'
import type { ReachState } from '../reach/types'

/** The home station of a new game: the home cluster's hub (as `newGameState` picks it). */
export function homeHubId(layout: Pick<SystemLayout, 'stations'>): string {
  const stations = layout.stations
  const hub = stations.find((s) => s.type === 'hub' && s.anchor.kind === 'cluster') ?? stations.find((s) => s.type === 'hub') ?? stations[0]
  return hub?.id ?? ''
}

let cached: { seed: number; state: ReachState } | null = null

/** A new game's Reach state on `galaxySeed` with no stand-in (a fresh copy; the last seed's is cached). */
export function freshReachState(galaxySeed: number): ReachState {
  const seed = galaxySeed >>> 0
  if (cached === null || cached.seed !== seed) {
    const galaxy = generateGalaxy(seed)
    const home = galaxy.systems.find((s) => s.id === galaxy.homeSystemId)
    if (home === undefined) throw new Error(`save: galaxy ${seed} has no home system`)
    const layout = buildSystemLayout(galaxy, home.index)
    const state = createReachState(galaxy, loadReachStateConfig(), { systemId: home.id, stationId: homeHubId(layout) }, null)
    cached = { seed, state }
  }
  return structuredClone(cached.state)
}

/** The canonical Reach's seed (`reach-state.json defaultGalaxySeed`). */
export function defaultGalaxySeed(): number {
  return loadReachStateConfig().defaultGalaxySeed
}

/** A record's galaxy seed when it is a uint32, else the canonical Reach's. */
export function recordGalaxySeed(raw: Record<string, unknown>): number {
  const seed = raw.galaxySeed
  return typeof seed === 'number' && Number.isInteger(seed) && seed >= 0 && seed <= 0xffffffff ? seed : defaultGalaxySeed()
}

/** Save format v1 → v2: adds a fresh `reach` block for the record's galaxy seed (and records that seed). */
export function migrateV1ToV2(raw: unknown): unknown {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) throw new Error('not a save record')
  const record = raw as Record<string, unknown>
  const galaxySeed = recordGalaxySeed(record)
  return { ...record, version: 2, galaxySeed, reach: freshReachState(galaxySeed) }
}
